import { useEffect } from 'react';

/**
 * useSeo — gestisce <title>, <meta description>, OG, Twitter, canonical e
 * JSON-LD per pagina senza dipendenze esterne.
 *
 * @param {Object} opts
 * @param {string} opts.title           Titolo pagina (obbligatorio)
 * @param {string} opts.description     Descrizione meta (obbligatorio)
 * @param {string} [opts.path]          Path canonico (default: window.location.pathname)
 * @param {string} [opts.image]         URL immagine OG (default: apple-touch-icon)
 * @param {string} [opts.type]          og:type (default: 'website')
 * @param {Object|Object[]} [opts.jsonLd]  Oggetto/array JSON-LD strutturato
 * @param {boolean} [opts.noindex]      Se true, aggiunge robots noindex
 */
const SITE_URL =
  process.env.REACT_APP_SITE_URL ||
  (typeof window !== 'undefined' ? window.location.origin : 'https://cirogallotti.com');

const DEFAULT_IMAGE = `${SITE_URL}/apple-touch-icon.svg`;
const BRAND_SUFFIX = ' | Ciro Gallotti — L\'Arte del Gioiello';

function upsertMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertJsonLd(id, data) {
  // Remove old
  document.querySelectorAll(`script[data-seo-id="${id}"]`).forEach((n) => n.remove());
  if (!data) return;
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.dataset.seoId = id;
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
}

export function useSeo({
  title,
  description,
  path,
  image,
  type = 'website',
  jsonLd,
  noindex = false,
}) {
  useEffect(() => {
    const canonicalPath = path || (typeof window !== 'undefined' ? window.location.pathname : '/');
    const canonicalUrl = `${SITE_URL}${canonicalPath}`;
    const fullTitle = title.includes('Ciro Gallotti') ? title : `${title}${BRAND_SUFFIX}`;
    const ogImage = image || DEFAULT_IMAGE;

    // Title
    document.title = fullTitle;

    // Meta tags
    upsertMeta('name', 'description', description);
    upsertMeta('name', 'robots',
      noindex
        ? 'noindex, nofollow'
        : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
    );

    // Open Graph
    upsertMeta('property', 'og:title', fullTitle);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', canonicalUrl);
    upsertMeta('property', 'og:type', type);
    upsertMeta('property', 'og:image', ogImage);
    upsertMeta('property', 'og:locale', 'it_IT');
    upsertMeta('property', 'og:site_name', 'Ciro Gallotti');

    // Twitter
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', fullTitle);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', ogImage);

    // Canonical
    upsertCanonical(canonicalUrl);

    // JSON-LD (page-scoped)
    upsertJsonLd('page', jsonLd || null);

    return () => {
      // Pulizia opzionale: rimuoviamo solo il JSON-LD page-specific al cambio pagina
      upsertJsonLd('page', null);
    };
  }, [title, description, path, image, type, JSON.stringify(jsonLd), noindex]);
}

export { SITE_URL };
